import React, {
	useContext,
	useMemo,
	useCallback,
	useRef,
	useEffect,
} from "react";
import clsx from "clsx";
import { Row, Col, Form } from "react-bootstrap";
import { Field, BaseFieldProps } from "redux-form";

import { Skeleton, RequiredMarkContext, RequiredMark } from "components";
import {
	ApplicationEntryProgressContext,
	BrandingColorContext,
} from "components/contexts";
import { CustomDropdownByValue } from "components/formFields/dropdown";
import ReadOnlyInput from "components/formFields/readOnlyInput";
import useLoader from "hooks/useLoader";

import useErrorMessage from "./useErrorMessage";
import useRangeValidation from "./useRangeValidation";
import FeeBlockContext from "../feeBlockContext";
import FeesTemplateContext from "../feesTemplateContext";

import LockIcon from "static/images/lock.png";
import styles from "./templateField.module.scss";

const TemplateInput = ({
	input,
	meta,
	name,
	placeholder,
	normalizeOnBlur,
	isLocked,
	isBrandingColor,
	placeholderClassName = null,
	triggerSave,
	disabled,
	isPlainText,
	solveCaretIssue,
	normalized,
	...props
}) => {
	const oldText = useRef("");

	useEffect(() => {
		oldText.current = input.value;
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (solveCaretIssue) {
			formatValueAfterSaveAndFirstload(
				input,
				normalized,
				solveCaretIssue,
			);
		}
	}, [input?.value, normalized]); // eslint-disable-line react-hooks/exhaustive-deps

	const formatValueAfterSaveAndFirstload = useCallback(
		(input, normalized, solveCaretIssue) => {
			const minimunLengthToStartNormalize = placeholder
				? placeholder.length + 3
				: 11;
			if (
				normalized &&
				solveCaretIssue &&
				input.value.indexOf(",") === -1 &&
				input.value.length >= minimunLengthToStartNormalize
			) {
				const result = normalized(input.value);
				input.onChange(result);
			}
		},
		[placeholder],
	);

	const handleOnBlur = e => {
		if (normalizeOnBlur) {
			e.target.value = normalizeOnBlur(e.target.value);
		}

		input.onBlur(e);
		triggerSave?.();
	};

	const { ErrorMessage, isInvalid } = useErrorMessage({ meta });

	const onChange = useCallback(
		event => {
			if (solveCaretIssue) {
				solveCaretIssue(event, input, oldText);
				return;
			}
			input.onChange(event);
		},
		[input, solveCaretIssue],
	);

	const showError =
		typeof meta.error === "string"
			? !disabled || (meta.touched && !disabled)
			: meta.message?.touched && meta.error?.isRequired && !disabled;

	const validationClass =
		showError &&
		((meta.error && styles.error) || (meta.warning && styles.warning));

	if (isLocked || disabled) {
		return (
			<ReadOnlyInput
				{...props}
				name={input.name}
				value={input.value}
				placeholder={placeholder}
				disabled={disabled}
				isLocked={isLocked}
				isPlainText={isPlainText}
			/>
		);
	} else {
		return (
			<>
				<Form.Control
					{...props}
					{...input}
					id={input.name}
					placeholder={placeholder}
					onChange={onChange}
					className={clsx(
						"w-100",
						validationClass,
						isPlainText && "isPlainText",
						isBrandingColor && "brandingColor",
						placeholderClassName &&
							!disabled &&
							placeholderClassName,
					)}
					isInvalid={isInvalid}
					onBlur={handleOnBlur}
					disabled={disabled}
				/>
				<ErrorMessage />
			</>
		);
	}
};

export type TemplateFieldProps = BaseFieldProps & {
	[prop: string]: any;
};

const TemplateField: React.FC<TemplateFieldProps> = ({
	name,
	type = "input",
	validate,
	validators,
	normalize,
	normalizeOnBlur,
	smSelect = 12,
	smLabel,
	label,
	items,
	hideRequiredMark,
	placeholderClassName,
	wrapperClassName,
	wrapperDdlClassName,
	disabled,
	isPlainText,
	solveCaretIssue,
	sign,
	...props
}) => {
	const { template, isFetching, findField, canUnlock, isEnabled } =
		useContext(FeesTemplateContext);
	const Loader = useLoader(isFetching);
	const isBrandingColor = useContext(BrandingColorContext);
	const showRequiredMark =
		useContext(RequiredMarkContext) && !hideRequiredMark && !disabled;
	const { triggerSave } = useContext(ApplicationEntryProgressContext);
	const fieldName = name;
	const blockName = useContext(FeeBlockContext);

	// prettier-ignore
	const field = useMemo(
		() => findField?.({ fieldName, blockName }),
		[findField, fieldName, blockName]
	);

	const { rangeValidator, otherValidators } = useRangeValidation({
		validators,
		field,
	});

	const isLocked = !!template && field?.locked;
	const allowedToEdit = !isLocked || canUnlock;

	const LockIconBlock = useCallback(() => {
		if (!isFetching && isLocked && !field?.forceLocked) {
			return (
				<div className={styles.lockBtn}>
					<img src={LockIcon} width="10" alt="LockIcon" />
				</div>
			);
		} else {
			return null;
		}
	}, [field?.forceLocked, isFetching, isLocked]);

	const TemplateFieldLabel = useCallback(
		({ disabled }) => {
			const disabledClassName = clsx(disabled && styles.disabled);

			if (label && typeof label === "string") {
				return (
					<Form.Label
						column
						htmlFor={name}
						className={disabledClassName}
					>
						{label}
						{showRequiredMark && <RequiredMark />}
						<div className={styles.sign}>{sign}</div>
					</Form.Label>
				);
			} else {
				return label || null;
			}
		},
		[label, name, showRequiredMark, sign],
	);

	const getValidateValue = () => {
		if (disabled || !allowedToEdit) {
			return undefined;
		}
		// the validate have higher priority over the validators
		else if (typeof validate === "object") {
			return validate;
		} else if (otherValidators) {
			return [rangeValidator, ...otherValidators];
		} else {
			return rangeValidator;
		}
	};

	return (
		<Row className={clsx(styles.templateGroup, wrapperClassName)}>
			<TemplateFieldLabel
				disabled={disabled || (isEnabled && !allowedToEdit)}
			/>
			<Col>
				<Loader
					skeleton={<Skeleton height={34} />}
					component={
						type === "dropdown" ? (
							<Field
								{...props}
								component={CustomDropdownByValue}
								name={name}
								validate={validate}
								disabled={disabled}
								readOnly={!allowedToEdit}
								items={items}
								smSelect={smSelect}
								smLabel={smLabel}
								wrapperClassName={`${wrapperDdlClassName} ${
									!allowedToEdit && styles.disabledSelect
								}`}
							/>
						) : (
							<Field
								{...props}
								component={TemplateInput}
								solveCaretIssue={solveCaretIssue}
								name={name}
								validate={getValidateValue()}
								normalize={normalize}
								normalized={normalize}
								normalizeOnBlur={normalizeOnBlur}
								isBrandingColor={isBrandingColor}
								placeholderClassName={placeholderClassName}
								triggerSave={triggerSave}
								disabled={disabled}
								isLocked={isEnabled && !allowedToEdit}
								isPlainText={isPlainText}
							/>
						)
					}
				/>
				{isEnabled && <LockIconBlock />}
			</Col>
		</Row>
	);
};

export default TemplateField;
